/**
 * SignedDocumentsCard — Sawyer 11 client-notes-agreements (F4).
 *
 * Sawyer, looking for a client's note, found the old "Agreements" card
 * instead: "These are agreements. Oh, yeah. Is that a note? ... I never go
 * to the agreement, so what's that doing here?" (memo2 [31:11]). That card
 * also lied: it read `acknowledged_agreement_version`, which only the login
 * re-ack writes, so a client who signed every document while booking read
 * "No agreement acknowledged yet" while the Front Desk said otherwise.
 *
 * This card says what it is ("Signed documents"), sits last on the
 * Overview, starts collapsed to one summary line (P3), and reads the SAME
 * standing the Front Desk chip is built from, per document, with the date
 * each one was signed (P1).
 *
 * States (DESIGN-PHILOSOPHY): loading ("Checking…"), error ("Couldn't load
 * signed documents" + Retry inside the card), empty ("No documents to
 * sign"), default. Deploy window: against a server without the route (403
 * route_not_mapped / 404) the card is not rendered at all and nothing logs.
 * P5: the Gingr sentence shows only for a Gingr-imported owner who has never
 * signed here; any other "unknown" is an error, never a claim about Gingr.
 */
import { useCallback, useEffect, useState, type JSX } from 'react';
import {
  fetchAgreementStanding,
  type AgreementStanding,
  type AgreementStandingDocument,
} from '../../lib/client-notes-api.js';
import { formatNoteDate } from './client-note-format.js';
import './ClientNotes.css';

export interface SignedDocumentsCardProps {
  readonly apiUrl: string;
  readonly token: string;
  readonly customerId: string;
}

type CardState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'hidden' }
  | { kind: 'ready'; standing: AgreementStanding };

export const GINGR_NOT_CARRIED =
  'No ALA signature on file. Agreements signed in Gingr were not carried over.';

/** Pure: the collapsed summary line for a loaded standing. */
export function signedDocumentsSummary(s: AgreementStanding): string {
  switch (s.standing) {
    case 'signed':
      return 'All signed';
    case 'not_required':
      return 'No documents to sign';
    case 'unsigned': {
      const names = s.documents.filter((d) => d.status !== 'ok').map((d) => d.display_name);
      return names.length > 0 ? `Needs signing: ${names.join(', ')}` : 'Needs signing';
    }
    case 'unknown':
      return s.imported && !s.ever_signed ? 'No ALA signature on file' : "Couldn't load signed documents";
  }
}

/** Pure: one document's line. */
export function documentLine(d: AgreementStandingDocument): string {
  switch (d.status) {
    case 'ok':
      return d.signed_at
        ? `Signed ${formatNoteDate(d.signed_at)} · version ${d.signed_version ?? d.required_version}`
        : `Signed · version ${d.signed_version ?? d.required_version}`;
    case 'missing':
      return 'Not signed';
    case 'outdated':
      return `Needs re-signing (signed version ${d.signed_version ?? '?'}, now version ${d.required_version})`;
  }
}

export function SignedDocumentsCard({ apiUrl, token, customerId }: SignedDocumentsCardProps): JSX.Element | null {
  const [state, setState] = useState<CardState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const r = await fetchAgreementStanding(apiUrl, token, customerId);
    if (r.kind === 'unavailable') setState({ kind: 'hidden' });
    else if (r.kind === 'error') setState({ kind: 'error' });
    // A failed standing read on the server is 'unknown'; unless that is the
    // true Gingr case it is an error, not a fact about the client (C6).
    else if (r.standing.standing === 'unknown' && !(r.standing.imported && !r.standing.ever_signed)) {
      setState({ kind: 'error' });
    } else setState({ kind: 'ready', standing: r.standing });
  }, [apiUrl, token, customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === 'hidden') return null;

  const summary =
    state.kind === 'loading'
      ? 'Checking…'
      : state.kind === 'error'
        ? "Couldn't load signed documents"
        : signedDocumentsSummary(state.standing);
  const tone =
    state.kind === 'ready' && state.standing.standing === 'signed'
      ? 'ok'
      : state.kind === 'ready' && state.standing.standing === 'unsigned'
        ? 'attention'
        : 'neutral';

  return (
    <article
      className="ferro-dashboard__card ferro-signeddocs"
      aria-label="Signed documents"
      data-testid="signed-documents-card"
    >
      <details className="ferro-signeddocs__details" data-testid="signed-documents-details">
        <summary className="ferro-signeddocs__summary" data-testid="signed-documents-summary">
          <span className="ferro-signeddocs__title">Signed documents</span>
          <span
            className={`ferro-signeddocs__rollup ferro-signeddocs__rollup--${tone}`}
            role={state.kind === 'loading' ? 'status' : undefined}
            data-testid="signed-documents-rollup"
          >
            {summary}
          </span>
        </summary>
        <div className="ferro-signeddocs__body">
          {state.kind === 'loading' ? (
            <p className="ferro-muted">Checking…</p>
          ) : state.kind === 'error' ? (
            <div className="ferro-alert ferro-alert--error" role="alert" data-testid="signed-documents-error">
              <span>Couldn&rsquo;t load signed documents.</span>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost ferro-signeddocs__retry"
                onClick={() => void load()}
                data-testid="signed-documents-retry"
              >
                Retry
              </button>
            </div>
          ) : state.standing.documents.length === 0 ? (
            <p className="ferro-muted" data-testid="signed-documents-empty">
              This business has no documents clients need to sign.
            </p>
          ) : (
            <>
              <ul className="ferro-signeddocs__list" data-testid="signed-documents-list">
                {state.standing.documents.map((d) => (
                  <li
                    key={d.agreement_id}
                    className={`ferro-signeddocs__doc ferro-signeddocs__doc--${d.status}`}
                    data-testid={`signed-document-${d.agreement_id}`}
                  >
                    <span className="ferro-signeddocs__doc-name">{d.display_name}</span>
                    <span className="ferro-signeddocs__doc-line">{documentLine(d)}</span>
                  </li>
                ))}
              </ul>
              {state.standing.standing === 'unknown' ? (
                <p className="ferro-muted" data-testid="signed-documents-gingr">
                  {GINGR_NOT_CARRIED}
                </p>
              ) : null}
            </>
          )}
        </div>
      </details>
    </article>
  );
}
